import { useState } from "react";
import { X, Sparkles, ArrowRight, Loader2, Zap } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

const DEFAULT_PACKS = {
  mfb_ai_wallet_pack_s: { credits: 10, amount_eur: 5, label: "Starter" },
  mfb_ai_wallet_pack_m: { credits: 25, amount_eur: 10, label: "Standard" },
  mfb_ai_wallet_pack_l: { credits: 60, amount_eur: 20, label: "Pro" },
};

const PACK_META = {
  mfb_ai_wallet_pack_s: {
    color: "border-slate-600 hover:border-amber-500 hover:bg-amber-500/5",
    accent: "text-slate-200",
    tag: null,
  },
  mfb_ai_wallet_pack_m: {
    color: "border-emerald-500/40 hover:border-emerald-400 hover:bg-emerald-500/10",
    accent: "text-emerald-300",
    tag: { label: "PIÙ SCELTO", cls: "bg-emerald-500 text-slate-950" },
  },
  mfb_ai_wallet_pack_l: {
    color: "border-amber-500/40 hover:border-amber-400 hover:bg-amber-500/10",
    accent: "text-amber-300",
    tag: { label: "MIGLIOR VALORE", cls: "bg-amber-500 text-slate-950" },
  },
};

export default function AIWalletTopupModal({ league, onClose, packs }) {
  const activePacks = packs || DEFAULT_PACKS;
  const [busy, setBusy] = useState(null); // pack id currently loading

  async function buy(packId) {
    setBusy(packId);
    try {
      const { data } = await api.post(
        `/leagues/${league.league_id}/ai-wallet/topup`,
        { pack: packId, origin_url: window.location.origin }
      );
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(null);
    }
  }

  const packOrder = ["mfb_ai_wallet_pack_s", "mfb_ai_wallet_pack_m", "mfb_ai_wallet_pack_l"];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm"
      onClick={onClose}
      data-testid="ai-topup-modal"
    >
      <div
        className="relative w-full max-w-3xl bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors z-10"
          data-testid="ai-topup-close"
        >
          <X size={22} />
        </button>

        <div className="p-6 sm:p-8 border-b border-slate-800 bg-gradient-to-br from-amber-500/[0.08] via-transparent to-emerald-500/[0.06]">
          <div className="text-[11px] tracking-[0.3em] uppercase text-amber-400 font-bold flex items-center gap-2">
            <Sparkles size={14}/> AI Wallet — Ricarica
          </div>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight mt-2">
            Scegli il pacchetto AI Coach
          </h2>
          <p className="mt-2 text-sm text-slate-400">
            Ogni chiamata è un parere completo di Claude Sonnet 5 sulla tua rosa (analisi Mantra + punti forza + sofferenze + suggerimenti tattici).
            I crediti non scadono e restano validi per l&apos;intera stagione della lega.
          </p>
        </div>

        <div className="p-6 sm:p-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {packOrder.filter(id => activePacks[id]).map((packId) => {
            const p = activePacks[packId];
            const meta = PACK_META[packId];
            const pricePerCall = (p.amount_eur / p.credits).toFixed(2);
            return (
              <button
                key={packId}
                onClick={() => buy(packId)}
                disabled={busy !== null}
                data-testid={`ai-topup-${packId}`}
                className={`relative rounded-2xl border p-5 text-left transition-all disabled:opacity-40 hover:-translate-y-0.5 ${meta.color}`}
              >
                {meta.tag && (
                  <div className={`absolute -top-2 left-4 text-[9px] px-2 py-0.5 rounded font-black tracking-widest ${meta.tag.cls}`}>
                    {meta.tag.label}
                  </div>
                )}
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">
                  Pack {p.label}
                </div>
                <div className="flex items-baseline gap-1">
                  <span className={`text-4xl font-black ${meta.accent}`}>{p.amount_eur}</span>
                  <span className="text-slate-500 text-lg font-bold">€</span>
                </div>
                <div className="mt-2 text-sm text-slate-300">
                  <span className="font-bold text-white">{p.credits}</span> chiamate AI
                </div>
                <div className="mt-1 text-[11px] text-slate-500 font-mono">
                  ~ {pricePerCall} € / chiamata
                </div>

                <div className="mt-4 pt-4 border-t border-slate-800 flex items-center justify-between text-xs">
                  <span className="text-slate-400">Attivazione istantanea</span>
                  {busy === packId ? (
                    <Loader2 size={14} className="animate-spin text-amber-400"/>
                  ) : (
                    <ArrowRight size={14} className={meta.accent}/>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <div className="px-6 sm:px-8 pb-6 flex items-center gap-2 text-xs text-slate-500">
          <Zap size={12} className="text-amber-400"/>
          Pagamento sicuro Stripe · I crediti restano nella tua lega <span className="text-slate-300">{league.name}</span> anche se cambi squadra
        </div>
      </div>
    </div>
  );
}
